import {skipToken} from '@reduxjs/toolkit/query';
import {getInitials} from 'platform/components';
import {Box, Center, Image, Integer, Show, Text} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';

import {useGetUserAvatarUrlQuery} from '@dms/api/customer';
import {SkeletonLoadingBox} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  SALES_LEAGUE_AVATAR_COLOR,
  SALES_LEAGUE_AVATAR_SIZE,
  SALES_LEAGUE_AVATAR_TEXT_COLOR,
} from '../../../constants/SALES_LEAGUE_AVATAR';
import {getSalesLeagueInitialsTextSize} from '../../../utils/getSalesLeagueInitialsTextSize';

/** Design-system units are 0.25rem, and the photo is requested at twice the drawn size. */
const PIXELS_PER_UNIT = 4;
const RETINA_SCALE = 2;

interface SalesLeagueAvatarProps extends RequiredTestIdProps {
  /** Nullish when there is no rep to show, e.g. a podium place nobody reached. */
  name: string | Nullish;
  /** Drives the profile photo lookup. Nullish falls back to initials. */
  userId: string | Nullish;
  /** Defaults to the table row's circle; the podium passes a size matching its text block. */
  size?: Integer;
}

/**
 * A rep's profile photo where they have one, their initials where they do not. There is no
 * "has a photo" field — the request simply fails for a rep without one, leaving the
 * initials showing, and every other consumer of this endpoint in DMS relies on the same
 * behaviour. `getUserAvatarList` batches the lookup but returns a uri per rep that still
 * needs its own blob fetch, so it would not remove the fan-out. Responses are cached for a
 * day, so a rep costs one small fetch per day however many boards they appear on.
 *
 * Built from primitives rather than platform `Avatar` because that offers four fixed sizes
 * and the podium circle has to match the height of the text beside it exactly. Initials
 * still come from platform's own `getInitials`, so a rep reads identically everywhere.
 */
export function SalesLeagueAvatar(props: SalesLeagueAvatarProps) {
  const size = props.size ?? SALES_LEAGUE_AVATAR_SIZE;
  const requestedPixels = size * PIXELS_PER_UNIT * RETINA_SCALE;

  const {data: photoUrl, isLoading} = useGetUserAvatarUrlQuery(
    isNotNil(props.userId)
      ? {userId: props.userId, width: requestedPixels, height: requestedPixels}
      : skipToken
  );

  if (isNil(props.name)) {
    return null;
  }

  // A rep with no photo resolves to an error rather than an empty result, so the skeleton
  // gives way to initials for them — it says "still looking", not "a photo is coming".
  if (isLoading) {
    return (
      <Box
        width={size}
        height={size}
        flexShrink={0}
        data-testid={suffixTestId('avatarSkeleton', props)}
      >
        <SkeletonLoadingBox isLoading width={size} height={size} borderRadius="circular" />
      </Box>
    );
  }

  return (
    <Box
      width={size}
      height={size}
      borderRadius="circular"
      backgroundColor={SALES_LEAGUE_AVATAR_COLOR}
      flexShrink={0}
      overflow="hidden"
      data-testid={suffixTestId('avatar', props)}
    >
      <Center width="100%" height="100%">
        <Show when={isNotNil(photoUrl)}>
          <Image src={photoUrl} width="100%" height="100%" fit="cover" isLazy />
        </Show>
        <Show when={isNil(photoUrl)}>
          <Text
            size={getSalesLeagueInitialsTextSize(size)}
            color={SALES_LEAGUE_AVATAR_TEXT_COLOR}
            alternative
            noWrap
          >
            {getInitials(props.name)}
          </Text>
        </Show>
      </Center>
    </Box>
  );
}
